import { addSourceFileAtSliderPosition } from 'actions/addSourceFileAtSliderPosition'
import FoldersTree from 'components/AssetPanels/Media/FoldersTree'
import styles from 'components/AssetPanels/Media/FoldersTree/folderTree.module.scss'
import Scrollbars from 'components/base/Scrollbars'
import { SelectDialog } from 'components/base/SelectDialog'
import { FILE_STATUS, FILE_TYPE, HOTKEYS, SOURCE_FILE_TYPES } from 'enums'
import { useHotkeyTooltip } from 'hooks/useHotkeyTooltip'
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSelector } from 'react-redux'
import * as Actions from '~/actions'
import { ReactComponent as DeleteIcon } from '~/assets/media/ic_delete.svg'
import { ReactComponent as MenuIcon } from '~/assets/media/ic_item_menu.svg'
import { ReactComponent as AddIcon } from '~/assets/media/ic_plus.svg'
import { ReactComponent as RetryIcon } from '~/assets/media/ic_vector.svg'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import { ConfirmationDialog } from '~/components/base/ConfirmationDialog/ConfirmationDialog'
import { TranslationContext } from '~/contexts/TranslationContext'
import { useAction, useOnClickWithoutPropagation } from '~/hooks'
import * as Selectors from '~/selectors'
import './MediaControls.scss'
import { MediaItemMenu } from './MediaItemMenu/MediaItemMenu'
import { BaseAssetsListContext } from '../BaseAssetsList'
import { useAddSourceFileToTimeline } from '~/hooks/useAddSourceFileToTimeline'
import { getDraggableType } from '~/helpers/getDraggableType'

export default function MediaControls({
  item,
  isFolder,
  useRemove = false,
  useMenu = false,
}) {
  const [ folderTreeLoading, setFolderTreeLoading ] = useState(true) // for Scrollbars rerender
  const itemMenuRef = useRef(null)

  const { t } = useContext(TranslationContext)
  const {
    setSelectedItems,
    onOpenContextMenu,
    onCloseContextMenu: onClose,
    clickedMediaElemId,
    menuPositionCoords,
    handleFolderSelect,
    selectedFolder,
    foldersDialog,
    errorTitle,
    errorMessage,
    singleDeletion,
    forceDeletionFolderDialog,
    setClickedMediaElemId,
    onMoveMediaItemsToFolder,
    isOpenContextMenu,
    isOpenDialog,
  } = useContext(BaseAssetsListContext)

  const onAddToTimeline = useAddSourceFileToTimeline(addSourceFileAtSliderPosition, [ item.id ])
  const onRetryUpload = useAction(Actions.upload.retryUpload, item.id)
  const openSaveSettings = useAction(Actions.mainView.showSaveSettings, true)
  const setProjectSelectedTask = useAction(Actions.project.setSelectedTask, item.taskId)

  const onShowSaveSettings = useCallback(() => {
    setProjectSelectedTask()
    openSaveSettings()
  }, [ setProjectSelectedTask, openSaveSettings ])

  const customMediaTabContent = useSelector(state => state.user.customMediaTabContent)
  const isMoveToEnabled = useSelector(state => Selectors.selectAreThereFolders(state, {
    type: SOURCE_FILE_TYPES.MEDIA,
    selectedItems: [ item.id ],
  }))
    && __CFG__.SOURCE_FILES_MANAGEMENT.MOVE_TO_FOLDERS
    && !customMediaTabContent


  const stopPropagation = useOnClickWithoutPropagation()

  const itemMediaAssetData = {
    id: item.id,
    name: item.name,
    type: getDraggableType(item),
    hasError: item.error,
    uploading: item.uploading,
    originalUrl: item.originalUrl || '',
    path: item.path || '',
    fileExtension: item.name?.split('.').at(-1),
    filetype: item.filetype,
  }

  const onDeleteItem = e => {
    e.stopPropagation()
    setSelectedItems(() => [ itemMediaAssetData ])
    setClickedMediaElemId(item.id)
    singleDeletion.open()
  }

  const onAddItemToTimeline = e => {
    setSelectedItems(() => [ itemMediaAssetData ])
    onAddToTimeline()
    onClose(e)
  }

  const onMoveMedia = useCallback(() => {
    foldersDialog.active
      ? foldersDialog.confirm(isFolder, item.id)
      : onMoveMediaItemsToFolder(isFolder, item.id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isFolder, item.id, foldersDialog ])

  const handleClickMoveFiles = useOnClickWithoutPropagation(() => {
    foldersDialog.open()
  })

  const currentFolder = useSelector(state => Selectors.selectCurrentFolderId(
    state, SOURCE_FILE_TYPES.MEDIA
  ))

  const isTopLayer = currentFolder === undefined

  const isOutputFiletype = item.type === FILE_TYPE.OUTPUT

  const selectedMediaItems = useMemo(() => [ item.id ], [ item.id ])

  const customMenuEnabled = (isTopLayer ? isOutputFiletype || isMoveToEnabled : true) && useMenu
  const { getTooltip } = useHotkeyTooltip()

  return (
    <>
      <div className="media-file__controls" id={item.id}>
        <If condition={useRemove}>
          <div
            title={getTooltip(t('MEDIA_CONFIRM_BTN_DELETE_TOOLTIP'), HOTKEYS.DELETE)}
            className="media-file__controls-item media-file__controls-item--delete"
            onClick={onDeleteItem}
            onMouseDown={stopPropagation}
            onMouseUp={stopPropagation}
          >
            <DeleteIcon />
          </div>
          <If condition={clickedMediaElemId === item.id && singleDeletion.active}>
            <ConfirmationDialog
              confirmArgs={{ isFolder, id: item.id }}
              onClick={stopPropagation}
              onCancel={singleDeletion.cancel}
              onConfirm={singleDeletion.confirm}
              confirmBtnText={t('MEDIA_CONFIRM_BTN_DELETE')}
              closeBtnText={t('MEDIA_CANCEL_BTN_DELETE')}
              description={t('MEDIA_CONFIRM_DESC_ARE_YOU_SURE_TO_REMOVE_SELECTED')}
            />
          </If>
          <If condition={clickedMediaElemId === item.id && forceDeletionFolderDialog.active}>
            <ConfirmationDialog
              onClick={stopPropagation}
              onCancel={forceDeletionFolderDialog.cancel}
              onConfirm={() => forceDeletionFolderDialog.confirm(item.id)}
              title={t(errorTitle)}
              description={t(errorMessage)}
              confirmBtnText={t('MEDIA_CONFIRM_BTN_DELETE')}
              closeBtnText={t('MEDIA_CANCEL_BTN_DELETE')}
            />
          </If>
        </If>

        <Choose>
          <When condition={item.error !== undefined}>
            <If condition={__CFG__.SOURCE_FILES_MANAGEMENT.RETRY_UPLOAD}>
              <div
                className="media-file__controls-item media-file__controls-item--retry"
                onClick={onRetryUpload}
                onMouseDown={stopPropagation}
                onMouseUp={stopPropagation}
              >
                <RetryIcon />
              </div>
            </If>
          </When>
          <When
            condition={(item.status === FILE_STATUS.READY)}
          >
            <div
              className="media-file__controls-item media-file__controls-item--add"
              onClick={onAddItemToTimeline}
              title={t('MEDIA_BTN_ADD_TOOLTIP')}
            >
              <AddIcon />
            </div>
          </When>
        </Choose>
      </div>
      <If condition={customMenuEnabled}>
        <div
          ref={itemMenuRef}
          className="media-file__controls media-file__controls--bottom"
        >
          <div
            title="Menu"
            className="media-file__controls-item media-file__controls-item--save"
          >
            <MenuIcon
              onClick={e => onOpenContextMenu(e, item.id)}
              onMouseDown={e => isFolder && stopPropagation(e)}
              onMouseUp={e => isFolder && stopPropagation(e)}
              id={item.id}
            />
          </div>
        </div>
      </If>
      <If condition={isFolder
        && isOpenContextMenu
        && !isOpenDialog
        && clickedMediaElemId === item.id}
      >
        <MediaItemMenu
          anchorEl={itemMenuRef.current}
          open={clickedMediaElemId === item.id}
          isFolder={isFolder}
          showMoveTo={isMoveToEnabled}
          isTopLayer={isTopLayer}
          showSettings={isOutputFiletype}
          onClickMoveFiles={handleClickMoveFiles}
          onClickShowSettings={onShowSaveSettings}
          onClickMoveToMedia={onMoveMedia}
          onClose={onClose}
          menuPositionCoords={menuPositionCoords}
        />
      </If>
      <If condition={isFolder && foldersDialog.active && clickedMediaElemId === item.id}>
        <SelectDialog
          title={t('MEDIA_CONFIRM_TITLE_BROWSER_FOR_FOLDER')}
          confirmBtnText={t('MEDIA_CONFIRM_BTN_OK')}
          onCancel={foldersDialog.cancel}
          onClick={stopPropagation}
          onMouseUp={stopPropagation}
          onConfirm={onMoveMedia}
          dataLangConfirmId="MEDIA_CONFIRM_BTN_OK"
          buttonConfirmTitle={t('MEDIA_CONFIRM_BTN_OK_TOOLTIP')}
        >
          <div className={styles.folderTreeContainer}>
            <Scrollbars>
              <FoldersTree
                onInit={setFolderTreeLoading}
                selectedFolder={selectedFolder}
                onSelect={handleFolderSelect}
                selectedMediaItems={selectedMediaItems}
              />
            </Scrollbars>
            <If condition={folderTreeLoading}>
              <div className={styles.folderTreeProgress}>
                <CircularProgress
                  size={100}
                  endless
                  percents={80}
                  text={`${t('MEDIA_PROGRESS_TEXT_LOADING')}..`}
                  transparent
                />
              </div>
            </If>
          </div>
        </SelectDialog>
      </If>
    </>
  )
}
